// /trip-setup?trip=<uuid> — screen 2 of trip setup.
//
// Auto-mounted at /trip-setup by the glob router in src/App.tsx. Query strings are
// not part of the route key, so this page reads `?trip=` itself.
//
// One screen, three jobs: the household roster (with people), the invite panel, and
// the "waiting on N households" banner.
//
// Everything here goes through the anon client, so RLS is exercised rather than
// bypassed. The `isOrganizer` checks below only avoid offering writes that would
// come back 403 — `household_write` / `person_write` in
// 20260808010300_rls.sql are the actual boundary.
import { useCallback, useEffect, useState, type JSX } from 'react'
import InvitePanel from '../components/InvitePanel'
import PeopleEditor from '../components/PeopleEditor'
import { AuthProvider, RequireAuth, useMembership } from '../lib/auth'
import { TripGateFallback, useResolvedTrip } from '../lib/useResolvedTrip'
import { supabase } from '../lib/supabaseClient'
import type { Household, Trip } from '../lib/types'

const css = `
.spz-setup { max-width: 34rem; margin: 0 auto; padding: 1.25rem 1rem 4rem; display: flex; flex-direction: column; gap: 1.25rem; }
.spz-setup h1 { font-size: 1.5rem; letter-spacing: -0.02em; }
.spz-setup h2 { font-size: 1.05rem; }
.spz-setup h3 { font-size: 1rem; }
.spz-setup .spz-muted { color: var(--muted); font-size: 0.9rem; }
.spz-setup .spz-error { color: #b3261e; font-size: 0.85rem; }
.spz-setup .spz-waiting { border: 1px solid color-mix(in srgb, #c07a1e 45%, transparent); background: color-mix(in srgb, #c07a1e 12%, transparent); border-radius: 0.75rem; padding: 0.85rem 1rem; font-size: 0.95rem; }
.spz-setup .spz-waiting strong { display: block; }
/* Answered, just not settled — informational, not a chase. Reads as a note rather
   than the amber warning used for households that have not engaged at all. */
.spz-setup .spz-waiting-soft { border-color: var(--line); background: transparent; color: var(--muted); }
.spz-setup .spz-hh { border: 1px solid var(--line); border-radius: 0.75rem; padding: 0.9rem 1rem; display: flex; flex-direction: column; gap: 0.6rem; }
.spz-setup .spz-hh-head { display: flex; flex-wrap: wrap; align-items: center; gap: 0.5rem; }
.spz-setup .spz-hh-head h3 { flex: 1 1 8rem; min-width: 0; overflow-wrap: anywhere; }
.spz-setup .spz-chip { font-size: 0.75rem; color: #8a5a10; border: 1px solid color-mix(in srgb, #c07a1e 45%, transparent); border-radius: 999px; padding: 0.1rem 0.55rem; white-space: nowrap; }
.spz-setup .spz-list { display: flex; flex-direction: column; gap: 0.75rem; }
.spz-setup form { display: flex; flex-direction: column; gap: 0.6rem; }
.spz-setup label { display: flex; flex-direction: column; gap: 0.25rem; font-size: 0.8rem; color: var(--muted); }
.spz-setup .spz-row { display: flex; gap: 0.6rem; }
.spz-setup .spz-row > * { flex: 1 1 0; min-width: 0; }
.spz-setup .spz-actions { display: flex; flex-wrap: wrap; gap: 0.5rem; }
.spz-setup button { min-height: 2.75rem; padding: 0.5rem 0.9rem; border-radius: 0.5rem; border: 1px solid color-mix(in srgb, currentColor 30%, transparent); background: transparent; color: inherit; }
.spz-setup button.spz-primary { border-color: var(--accent); background: var(--accent); color: var(--ground); font-weight: 600; }
.spz-setup button:disabled { opacity: 0.55; cursor: default; }
`

/** Every column of Trip / Household — a partial select would make the casts a lie. */
const TRIP_COLUMNS =
  'id, name, year, status, organizer_ids, currency, horizon_start, horizon_end, created_at'
const HOUSEHOLD_COLUMNS =
  // Every column of Household. The rows are cast to Household, and a cast cannot
  // check a string — so a column missing here is `undefined` at runtime while
  // TypeScript insists it is present. Add new columns here in the same PR that
  // adds them to the type.
  'id, trip_id, label, origin_town, origin_zip, origin_lat, origin_lng, max_drive_hours, modes_ok, nearest_airports, house_requirements_raw, house_nice_to_have_raw, location_must_have_raw, location_nice_to_have_raw, dates_pending, created_at'


/** "Ashland, MA 01721" from whichever origin parts exist. Town + ZIP only, never a street. */
function originLabel(household: Household): string | null {
  const parts = [household.origin_town, household.origin_zip].filter(
    (part): part is string => Boolean(part && part.trim()),
  )
  return parts.length > 0 ? parts.join(' ') : null
}

interface HouseholdDraft {
  label: string
  origin_town: string
  origin_zip: string
}

const emptyDraft: HouseholdDraft = { label: '', origin_town: '', origin_zip: '' }

function TripSetupInner({ tripId }: { tripId: string }): JSX.Element {
  const { membership, loading: membershipLoading } = useMembership(tripId)
  const isOrganizer = membership?.role === 'organizer'

  const [trip, setTrip] = useState<Trip | null>(null)
  const [households, setHouseholds] = useState<Household[]>([])
  /** Household ids that have at least one availability row. */
  const [withAvailability, setWithAvailability] = useState<Set<string> | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const [addDraft, setAddDraft] = useState<HouseholdDraft>(emptyDraft)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editDraft, setEditDraft] = useState<HouseholdDraft>(emptyDraft)

  const refresh = useCallback(async () => {
    const [householdRes, availabilityRes] = await Promise.all([
      supabase
        .from('household')
        .select(HOUSEHOLD_COLUMNS)
        .eq('trip_id', tripId)
        .order('label', { ascending: true }),
      supabase.from('availability').select('household_id').eq('trip_id', tripId),
    ])

    if (householdRes.error) {
      setError(householdRes.error.message)
    } else {
      setError(null)
      setHouseholds((householdRes.data ?? []) as unknown as Household[])
    }

    // A failed availability read must not turn every household amber, so the
    // banner stays hidden (null) rather than guessing.
    if (availabilityRes.error) {
      setWithAvailability(null)
    } else {
      const rows = (availabilityRes.data ?? []) as unknown as Array<{ household_id: string }>
      setWithAvailability(new Set(rows.map((row) => row.household_id)))
    }
  }, [tripId])

  useEffect(() => {
    let cancelled = false

    void (async () => {
      const { data, error: err } = await supabase
        .from('trip')
        .select(TRIP_COLUMNS)
        .eq('id', tripId)
        .maybeSingle()
      if (cancelled) return

      if (err || !data) {
        // trip_select is scoped to members, so "not found" and "not yours" are the
        // same 0-row answer from here.
        setError(err?.message ?? 'Trip not found, or you are not a member of it.')
        setLoading(false)
        return
      }

      setTrip(data as unknown as Trip)
      await refresh()
      if (!cancelled) setLoading(false)
    })()

    return () => {
      cancelled = true
    }
  }, [tripId, refresh])

  async function addHousehold(e: React.FormEvent) {
    e.preventDefault()
    const label = addDraft.label.trim()
    if (!label) {
      setError('Household name is required')
      return
    }
    setBusy(true)
    setError(null)
    const { error: err } = await supabase.from('household').insert({
      trip_id: tripId,
      label,
      origin_town: addDraft.origin_town.trim() || null,
      origin_zip: addDraft.origin_zip.trim() || null,
    })
    setBusy(false)
    if (err) {
      setError(err.message)
      return
    }
    setAddDraft(emptyDraft)
    await refresh()
  }

  async function saveHousehold(e: React.FormEvent) {
    e.preventDefault()
    if (!editingId) return
    const label = editDraft.label.trim()
    if (!label) {
      setError('Household name is required')
      return
    }
    setBusy(true)
    setError(null)
    const { error: err } = await supabase
      .from('household')
      .update({
        label,
        origin_town: editDraft.origin_town.trim() || null,
        origin_zip: editDraft.origin_zip.trim() || null,
      })
      .eq('id', editingId)
    setBusy(false)
    if (err) {
      setError(err.message)
      return
    }
    setEditingId(null)
    setEditDraft(emptyDraft)
    await refresh()
  }

  async function deleteHousehold(household: Household) {
    if (!window.confirm(`Delete ${household.label}? This also removes their people and dates.`)) {
      return
    }
    setBusy(true)
    setError(null)
    const { error: err } = await supabase.from('household').delete().eq('id', household.id)
    setBusy(false)
    if (err) {
      // person and availability are ON DELETE CASCADE, but membership.household_id
      // is ON DELETE SET NULL — say what actually blocked it rather than leaving a
      // dead button.
      const blocked = err.code === '23503' || /foreign key/i.test(err.message)
      setError(
        blocked
          ? `Something still references ${household.label}. Remove it first, then delete the household.`
          : err.message,
      )
      return
    }
    await refresh()
  }

  if (loading || membershipLoading) {
    return (
      <main className="spz-setup">
        <style>{css}</style>
        <p className="spz-muted">Loading trip&hellip;</p>
      </main>
    )
  }

  if (!trip) {
    return (
      <main className="spz-setup">
        <style>{css}</style>
        <h1>Trip setup</h1>
        <p className="spz-error" role="alert">
          {error ?? 'Trip not found.'}
        </p>
        <p>
          <a href="/trips">Back to trips</a>
        </p>
      </main>
    )
  }

  // A household is "waiting" when its intake is incomplete: no origin stated, or
  // not one availability row yet. This banner is the entire nudge mechanism the
  // family agreed to — no notifications, no reminder emails, no chasing.
  //
  // Split in two, because the two need different responses. A household that has
  // ticked "we don't know our dates yet" has engaged and answered everything it
  // can; resending the invite link achieves nothing. One that has not is either
  // yet to open the link or stalled partway. They looked identical before, so the
  // only available response was to chase both the same way.
  const waiting =
    withAvailability === null
      ? []
      : households.filter((h) => originLabel(h) === null || !withAvailability.has(h.id))
  const datesPromised = waiting.filter((h) => h.dates_pending && originLabel(h) !== null)
  const notStarted = waiting.filter((h) => !(h.dates_pending && originLabel(h) !== null))
  const waitingIds = new Set(waiting.map((h) => h.id))

  return (
    <main className="spz-setup">
      <style>{css}</style>

      <div>
        <p className="spz-muted">
          <a href="/trips">← All trips</a>
        </p>
        <h1>{trip.name}</h1>
        <p className="spz-muted">
          {trip.year} · {trip.currency} · horizon {trip.horizon_start} → {trip.horizon_end}
          {membership ? ` · you are ${membership.role}` : ''}
        </p>
      </div>

      {notStarted.length > 0 && (
        <p className="spz-waiting" role="status">
          <strong>
            Waiting on {notStarted.length} {notStarted.length === 1 ? 'household' : 'households'}
          </strong>
          {notStarted.map((h) => h.label).join(', ')} — no origin or no dates yet. Send them the
          invite link below; nothing else chases them.
        </p>
      )}

      {datesPromised.length > 0 && (
        <p className="spz-waiting spz-waiting-soft" role="status">
          <strong>
            Dates to come from {datesPromised.length}{' '}
            {datesPromised.length === 1 ? 'household' : 'households'}
          </strong>
          {datesPromised.map((h) => h.label).join(', ')} — everything else is in, and they said
          their dates aren&rsquo;t settled yet. The invite link won&rsquo;t help; ask them when
          they&rsquo;ll know.
        </p>
      )}

      <InvitePanel tripId={trip.id} isOrganizer={isOrganizer} households={households} />

      <section className="spz-list">
        <h2>Households</h2>
        {households.length === 0 && (
          <p className="spz-muted">No households yet. Add one for each family group.</p>
        )}

        {households.map((household) => (
          <div className="spz-hh" key={household.id}>
            {editingId === household.id ? (
              <form onSubmit={(e) => void saveHousehold(e)}>
                <label>
                  Household name
                  <input
                    type="text"
                    required
                    autoFocus
                    value={editDraft.label}
                    onChange={(e) => setEditDraft({ ...editDraft, label: e.target.value })}
                  />
                </label>
                <div className="spz-row">
                  <label>
                    Town
                    <input
                      type="text"
                      value={editDraft.origin_town}
                      onChange={(e) => setEditDraft({ ...editDraft, origin_town: e.target.value })}
                    />
                  </label>
                  <label>
                    ZIP
                    <input
                      type="text"
                      inputMode="numeric"
                      value={editDraft.origin_zip}
                      onChange={(e) => setEditDraft({ ...editDraft, origin_zip: e.target.value })}
                    />
                  </label>
                </div>
                <div className="spz-actions">
                  <button type="submit" className="spz-primary" disabled={busy}>
                    Save
                  </button>
                  <button type="button" onClick={() => setEditingId(null)} disabled={busy}>
                    Cancel
                  </button>
                </div>
              </form>
            ) : (
              <>
                <div className="spz-hh-head">
                  <h3>{household.label}</h3>
                  {waitingIds.has(household.id) && (
                    <span className="spz-chip">intake incomplete</span>
                  )}
                  {isOrganizer && (
                    <>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => {
                          setEditingId(household.id)
                          setEditDraft({
                            label: household.label,
                            origin_town: household.origin_town ?? '',
                            origin_zip: household.origin_zip ?? '',
                          })
                        }}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => void deleteHousehold(household)}
                      >
                        Delete
                      </button>
                    </>
                  )}
                </div>
                <p className="spz-muted">{originLabel(household) ?? 'No origin yet'}</p>
              </>
            )}

            <PeopleEditor householdId={household.id} readOnly={!isOrganizer} />
          </div>
        ))}

        {error && (
          <p className="spz-error" role="alert">
            {error}
          </p>
        )}

        {isOrganizer && (
          <form onSubmit={(e) => void addHousehold(e)}>
            <label>
              Add a household
              <input
                type="text"
                placeholder="The Carters"
                value={addDraft.label}
                onChange={(e) => setAddDraft({ ...addDraft, label: e.target.value })}
              />
            </label>
            <div className="spz-row">
              <label>
                Town (optional)
                <input
                  type="text"
                  value={addDraft.origin_town}
                  onChange={(e) => setAddDraft({ ...addDraft, origin_town: e.target.value })}
                />
              </label>
              <label>
                ZIP (optional)
                <input
                  type="text"
                  inputMode="numeric"
                  value={addDraft.origin_zip}
                  onChange={(e) => setAddDraft({ ...addDraft, origin_zip: e.target.value })}
                />
              </label>
            </div>
            <div className="spz-actions">
              <button type="submit" className="spz-primary" disabled={busy || !addDraft.label.trim()}>
                Add household
              </button>
            </div>
            <p className="spz-muted">
              Town and ZIP only — SPZ never stores a street address.
            </p>
          </form>
        )}
      </section>
    </main>
  )
}

function TripSetupResolved(): JSX.Element {
  // ?trip= still wins; otherwise fall back to the signed-in user's membership rather than
  // dead-ending, which is what a direct visit or a bookmark used to do.
  const resolution = useResolvedTrip()
  if (resolution.kind !== 'ready') {
    return <TripGateFallback resolution={resolution} what="trip setup" />
  }
  return <TripSetupInner tripId={resolution.tripId} />
}

export default function TripSetupPage(): JSX.Element {
  // Self-wrapped AuthProvider (nest-safe) so the page works before a root provider
  // is mounted in main.tsx.
  return (
    <AuthProvider>
      <RequireAuth>
        <TripSetupResolved />
      </RequireAuth>
    </AuthProvider>
  )
}
